// Tour system types. See CONTEXT.md and docs/adr/0001-tour-config-vs-state-decoupling.md.
//
// Three layers:
//   C (this file's config types) — authored, source of truth.
//   B (TourState)                — runtime state, per tour.
//   A                            — no standalone structure; the config array IS the graph.
//                                  `edgesOf`/`validateConfig` (edges.ts) read it directly.

import type { Entitlement } from '@/shared/entitlements';

/**
 * A condition's answer. `pending` is NOT a third kind of false: a `false` variant falls through
 * to the next one, but a `pending` variant stops resolution and renders nothing. That difference
 * is the whole point — without it, a predicate reading data that has not arrived yet picks the
 * catch-all, shows the wrong copy, and then corrects itself in front of the user.
 */
export type CondResult = boolean | 'pending';

/**
 * Every component config may be gated to a path pattern, a named condition, or both (they AND).
 * A component's `ui` entry is either a single config or an array of variants; the runtime picks
 * the FIRST that matches. A config with neither gate is a catch-all — put it last.
 * `*` matches within a segment, `**` across segments (see route.ts).
 *
 * `when` and `cond` are deliberately separate rather than one predicate. `when` is not merely a
 * test: `stepHomePath` (backTarget.ts) reads a URL back OUT of it, because Go Back is the one
 * transition with no authored path and has to work out which page to navigate to first. A
 * predicate is opaque, so a `cond` could never serve that purpose.
 */
export interface PathGated {
  /** One pattern, or several (any match wins) — e.g. the prod and dev habitat paths. */
  when?: string | string[];
  /**
   * Name of a predicate in the condition registry (core/conditions.ts), evaluated against the
   * current tour context. Gates on app state the way `when` gates on path.
   */
  cond?: string;
  /**
   * Matched, but intentionally inert: the component renders nothing (and headless ones do not
   * run) on these paths. Place a hidden variant BEFORE a catch-all to punch a hole in it.
   * Not needed merely to render nothing off-path — omitting a catch-all already does that.
   */
  hidden?: boolean;
}

/** A bare sentinel variant: matches a path purely to suppress its component there. */
export interface HiddenVariant extends PathGated {
  hidden: true;
}

/** One config, or several path-varied ones tried in order. */
export type Varied<T> = T | T[];

/** A button rendered inside a banner. `targetStepId` is the authored edge. */
export interface BannerButton {
  label: string;
  /**
   * Named condition gating this individual button. Omitted when false — the rest of the banner
   * still renders, which is why this exists rather than duplicating the whole banner as two
   * variants just to drop one option.
   */
  cond?: string;
  navigatePath?: string; // may contain the [username] placeholder
  devNavigatePath?: string; // may contain the [username] placeholder
  targetStepId?: string; // the edge; falls back to the step's implicit next when absent
  forceReload?: boolean; // hard window.location navigation (state flushed first)
  restartTour?: boolean; // restart a tour from its first step
  /** Which tour `restartTour` applies to. Defaults to the tour owning this step. */
  restartTourId?: string;
  /** Mark the owning tour completed (terminal). Lets another tour become eligible. */
  completeTour?: boolean;
  /**
   * Override the analytics action this button emits (default 'button-clicked'). Restores the
   * pre-refactor event names now that pause/resume/skip-ahead are ordinary buttons.
   */
  trackAction?: string;
  /**
   * Pop the history stack instead of naming a target. The one genuine mechanism: at a merge
   * point (e.g. BB1 is reachable from 8 different steps) only the recorded path knows where
   * the user came from, so a pre-authored targetStepId cannot express "back".
   */
  goBack?: boolean;
  /**
   * Paused banner only: which control is the filled one. Omitted everywhere means the last button
   * is primary, as it was before a banner carried three. Needed once the primary is not last —
   * "Finish the Tour" sits to the right of Continue but is the quieter of the two.
   */
  emphasis?: 'primary' | 'secondary';
}

/** Mascot-led speech banner. Advances on button click. */
export interface GuideBannerConfig extends PathGated {
  type: 'guide';
  mascot: 'talking' | 'idle';
  speechBubble?: string;
  buttons?: BannerButton[];
  /** Rendered in the banner's dedicated back slot. */
  backButton?: BannerButton;
  /** Rendered in the banner's dedicated "take a break" slot. */
  breakButton?: BannerButton;
}

/**
 * A guide-style banner whose single button, instead of navigating, buys the recommended habitat's
 * single-habitat-day-pass with `offerId` applied and redirects to Stripe. Renders through the same
 * presentational shell as `GuideBannerConfig`; the app-specific checkout lives in its component so
 * the tour core stays free of product/Stripe concepts.
 */
export interface OfferCheckoutBannerConfig extends PathGated {
  type: 'offer-checkout';
  mascot: 'talking' | 'idle';
  speechBubble?: string;
  buttonLabel: string;
  /** Offer id applied at checkout (case-insensitive; lowercased before the request). */
  offerId: string;
}

/** Headless timer. Advances to `targetStepId` after `delayMs`. */
export interface AutoAdvanceConfig extends PathGated {
  delayMs: number;
  targetStepId: string;
}

/** Headless media-close listener. Advances when an open media modal closes. */
export interface OnMediaCloseConfig extends PathGated {
  targetStepId: string;
}

/**
 * Highlight a page element while this step is showing. `target` names the element via its
 * `data-tour-target` attribute; several elements may share one name and glow together (the
 * capture step lights the photo, clip and react buttons at once).
 */
export interface SpotlightConfig extends PathGated {
  target: string;
  /** Bring the target into view once on mount when it is off-screen. Default false. */
  scrollIntoView?: boolean;
}

/**
 * Headless app-event listener. Advances when the user actually performs an action, rather than
 * clicking a banner button — "now zoom in" is answered by zooming, not by pressing Next.
 *
 * Deliberately transient: an event fired before this step mounts is not remembered. That is
 * correct for onboarding, where the step is *asking* the user to do the thing.
 */
export interface OnAppEventConfig extends PathGated {
  event: TourAppEventName;
  targetStepId: string;
  /**
   * Tour ids to mark completed when this fires, besides advancing. Lets a step declare that the
   * action it is waiting for retires OTHER tours too — taking a clip is the habitat onboarding's
   * success condition in every scenario, not just the one the user happens to be in.
   *
   * Never name the owning tour here: completing clears it, the arbiter drops it, and whatever it
   * was rendering vanishes mid-step. A tour ends itself with a terminal `completesTour` step.
   */
  completeTours?: string[];
}

/** The app events a tour step may wait on. A subset of AppEvent — tours never wait on logout. */
export type TourAppEventName =
  | 'habitat:camera-zoomed'
  | 'habitat:camera-panned'
  | 'habitat:camera-switched'
  | 'habitat:media-captured'
  | 'habitat:media-saved'
  | 'habitat:media-discarded'
  | 'habitat:tab-changed'
  | 'habitat:followed';

/** Full-screen modal rendered outside the router. */
export interface ModalConfig extends PathGated {
  kind: 'welcome' | 'tzWelcome' | 'feedback' | 'video';
  /**
   * The modal's primary action, expressed as an ordinary button so it navigates, reports and
   * advances through the same path as every banner button. Needed because a welcome modal's next
   * step does not always live on the page the modal is covering — the TZ+ tour's first step stands
   * in a habitat, not on `/home`. Takes precedence over `nextStepId`, which stays for the modals
   * whose action only advances.
   */
  button?: BannerButton;
  /** Video modal only: the demo to play. Closing it, or reaching the end, advances. */
  src?: string;
  /** Where the modal's primary action advances to (e.g. submitting feedback). */
  nextStepId?: string;
  /** Where dismissing/finishing the modal advances to. Defaults to `nextStepId`. */
  onCloseStepId?: string;
  /** Feedback modal only: render its post-submission state. */
  submitted?: boolean;
}

/**
 * The collapsed paused banner. There is no pause STATE: this renders either because an
 * earlier (path-gated) variant did not match, or because the step is a dedicated paused step
 * reached via a button. Resume is just a button — `goBack: true` to pop back, or
 * `navigatePath` to return to the path so the guide variant matches again.
 */
export interface PausedBannerConfig extends PathGated {
  type: 'paused';
  currentSection: string;
  nextHabitat?: string;
  /** e.g. "Explore with Guide" (goBack or navigatePath) and "Skip Ahead" (targetStepId). */
  buttons?: BannerButton[];
}

/**
 * Both kinds render into the SAME banner slot, so they are variants of one `ui.banner` entry
 * rather than separate components. First match wins, which guarantees exactly one banner can
 * ever show and removes any need for negative path matching.
 */
export type BannerVariant = GuideBannerConfig | PausedBannerConfig | OfferCheckoutBannerConfig | HiddenVariant;

/** What a resolved banner can be — hidden variants never survive resolution. */
export type VisibleBannerVariant = GuideBannerConfig | PausedBannerConfig | OfferCheckoutBannerConfig;

/** One position in a tour. Composes any subset of the per-component configs. */
export interface TourStep {
  id: string;
  /**
   * If set, restoring persisted state onto this step advances to `restoreTo` instead.
   * Generalizes the legacy "don't return to a stuck feedback modal" transform — mark a
   * transient/modal step so a reload lands somewhere recoverable.
   */
  restoreTo?: string;
  /**
   * Restoring onto this step marks the tour completed instead. Used for transient modal steps
   * that are also the tour's end — a reload must not strand the user inside the modal.
   */
  restoreCompletes?: boolean;
  /**
   * Entering this step marks the tour completed (and therefore cleared).
   *
   * Put it on a step with no `ui`, never on the final banner: completing clears the tour, the
   * arbiter drops it, and anything it was rendering disappears at once. A closing banner
   * therefore auto-advances to a bare step carrying this flag.
   */
  completesTour?: boolean;
  /**
   * Extra payload merged into events emitted for this step. Analytics-only — nothing in the
   * runtime reads these.
   *
   * `stepNumber` is the step's position along the journey and feeds the monotonic furthest mark,
   * so it must reflect progress rather than position in the config array. A step that sits off
   * the main line (the paused steps) uses **-1**, which means "no position of my own": it can
   * never advance the furthest mark, and in events it reports the last real step instead, so a
   * break taken at step 12 is recorded at 12 rather than as a sentinel.
   */
  analytics?: { stepNumber?: number; section?: string; nextHabitat?: string; [key: string]: unknown };
  /**
   * Per-component render config. Runtime state picks which of these wins on a given render
   * (e.g. paused -> pausedBanner, otherwise banner); several may coexist on one step.
   */
  ui: {
    banner?: Varied<BannerVariant>;
    autoAdvance?: Varied<AutoAdvanceConfig>;
    onMediaClose?: Varied<OnMediaCloseConfig>;
    modal?: Varied<ModalConfig>;
    spotlight?: Varied<SpotlightConfig>;
    onAppEvent?: Varied<OnAppEventConfig>;
  };
}

/** Authored tour config (layer C): identity + ordered steps. */
export interface TourConfig {
  id: string;
  steps: TourStep[];
}

/**
 * Durable, cross-device analytics. Server-owned: arrives on the user object as
 * `user.tours[tourId]` and overlays the client state via selectTour.
 */
export interface TourAnalytics {
  /**
   * Last known position, mirrored from `stepHistory.at(-1)`. Sent to the server so progress is
   * legible cross-device without shipping the whole history. The runtime never reads this —
   * position always comes from stepHistory — so a stale value cannot misdirect a tour.
   */
  currentStepId?: string;
  /**
   * High-water mark of progress. Stored rather than derived from stepHistory for two reasons:
   * the history is capped, so a deep step can be evicted and make a derived value regress; and
   * the server never receives the history, so it could not compute this.
   */
  furthestStepId?: string;
  furthestStepNumber?: number;
  // ISO 8601 date strings, not epoch numbers — the API accepts date strings and Mixpanel
  // renders them as dates rather than opaque integers.
  startedAt?: string;
  lastStartedAt?: string;
  skippedAt?: string; // terminal, clears the arbitration gate
  completedAt?: string;
  lastCompletedAt?: string;
  completedTimes: number;
}

/**
 * A partial update to a tour's server-side analytics. Only changed fields travel; `null` is an
 * explicit clear (restart wipes `completedAt`/`skippedAt`/`furthest*`), which `undefined` cannot
 * express because it vanishes from JSON and would read as "leave this alone".
 */
export type TourAnalyticsPatch = { [K in keyof TourAnalytics]?: TourAnalytics[K] | null };

/**
 * Runtime state for a single tour (layer B). The client slice owns the fat history stack
 * and the transient pause; analytics fields (see TourAnalytics) are overlaid from the user
 * object. deriveStatus reads the merged shape.
 */
export interface TourState extends TourAnalytics {
  /** Visited step ids; last element is the current step. Client-only, capped. */
  stepHistory: string[];
}

export type TourStatus = 'notStarted' | 'active' | 'skipped' | 'completed';

/** Where a tour may appear. A predicate over the current pathname, or everywhere. */
export type TourScope = 'global' | ((pathname: string) => boolean);

/**
 * The only redux state a tour's gate predicates (`isEligible` / `autoStart`) may read.
 *
 * Deliberately narrow: the arbiter subscribes to exactly these inputs and memoizes on them
 * (see selectTourGate), so a gate that reached into the whole store would re-run arbitration on
 * every dispatch in the app. Adding a field here is allowed — it just widens what wakes the
 * arbiter, which should be a conscious choice.
 */
export interface TourGateState {
  /**
   * Whether the user holds an entitlement now — the shared ability's `can`, so a tour asks exactly
   * what every other gate in the app asks (`tour.default`, `tour.tzPlus`), and a pass lapsing
   * mid-visit is seen at the next check.
   */
  can: (entitlement: Entitlement) => boolean;
  /** Merged per-tour state (client progress + server analytics), keyed by tour id. */
  tours: Record<string, TourState>;
}

/**
 * Which of the three habitat scenarios the page is in. Live stream wins over schedule: a
 * habitat that is visibly streaming is `online` even with no schedule configured, because the
 * user can actually zoom, pan and capture — the online copy is the only actionable one.
 *
 *   isStreamOn && camera on            -> 'online'
 *   has schedule && now outside it     -> 'asleep'
 *   otherwise                          -> 'outage'   (no schedule; or in-window but camera down)
 */
export type HabitatTourMode = 'online' | 'asleep' | 'outage';

/**
 * A habitat to send the user to next. Present whenever any other habitat exists — accessible or
 * not — with the accessible ones preferred. `accessible` is what splits the ending: an accessible
 * suggestion goes straight there (10A), a locked one is offered a free visit when the user is
 * eligible for the explorer offer, and its animal names the offer. `_id` lets the checkout banner
 * look up this habitat's single-habitat-day-pass product.
 */
export interface RecommendedHabitat {
  _id: string;
  animal: string;
  zooSlug: string;
  slug: string;
  accessible: boolean;
}

/**
 * What the habitat page tells the tours about itself. Deliberately narrow and flat: spreading
 * `habitatInfo` would drag in socket-updated fields (`currentViewers` ticks constantly), and
 * every identity change re-runs every condition.
 */
export interface HabitatTourContext {
  /**
   * Staleness guard. `HabitatPage` stays mounted across habitat -> habitat navigation
   * (preact-router re-renders it with new params), so unmount cleanup does not fire and a
   * previous habitat's context can linger for a frame. Conditions compare this against the
   * habitat in the store and answer `pending` when they disagree.
   */
  habitatId: string;
  mode: HabitatTourMode;
  zooName: string;
  country: string;
  /** Formatted stream window, e.g. "9am - 5pm". Resolves asynchronously; absent -> pending. */
  streamTimes?: string;
  cameraCount: number;
  isFollowing: boolean;
  /** How many habitats this user has visited before, from the visitedHabitatIds cookie. */
  visitedHabitatCount: number;
  /** Joined more than 24h ago — selects the "existing user" welcome copy. */
  isExistingUser: boolean;
}

/**
 * The context a tour reads to branch and to fill copy. Page-supplied and swappable — the
 * habitat page republishes it per habitat. Absent, or missing the field a condition needs,
 * means `pending` rather than `false`.
 */
export interface TourContext {
  habitat?: HabitatTourContext;
  recommendedHabitat?: RecommendedHabitat;
  /**
   * Whether the user may redeem the explorer offer, from `/users/me/offers/explorer`. Tri-state by
   * absence: undefined (key absent) while the query is in flight means `pending`, so the ending
   * shows nothing rather than flashing the wrong copy; `true`/`false` once settled. Errors publish
   * `false` so the user can always finish the tour.
   */
  explorerOfferEligible?: boolean;
  /**
   * A modal is holding the screen (upsell, members-only warning, …). While true the habitat
   * onboarding steps aside: a banner behind a modal backdrop, glowing at elements the user
   * cannot reach, with its timers still running, is worse than nothing. It resumes where it
   * left off when the modal closes.
   */
  blockingModalOpen?: boolean;
}

/** A named predicate over the tour context. See core/conditions.ts. */
export type TourCondition = (ctx: TourContext | undefined) => CondResult;

/** Arbitration metadata for a tour (layer registry, not persisted). */
export interface TourRegistration {
  id: string;
  /** Analytics name. A tour without one reports nothing — new tours stay silent by default. */
  name?: string;
  config: TourConfig;
  scope: TourScope;
  exclusive: boolean; // while active, suppresses all other tours on screen
  priority: number; // higher wins within a slot
  firstStepId: string; // where startTour begins
  /**
   * Extra gate, e.g. a subscription feature or the habitat's current scenario. May answer
   * `pending` while the page has not published enough context to decide — which excludes only
   * THIS tour (identically to `false` here), never anything else on screen.
   */
  isEligible?: (gate: TourGateState, ctx: TourContext | undefined, pathname: string) => CondResult;
  /**
   * Should this tour start now? Evaluated for eligible, not-yet-started tours. Replaces
   * per-tour bespoke start logic in the slot. A tour gated entirely by `isEligible` (the
   * habitat onboardings) just returns true here.
   */
  autoStart?: (gate: TourGateState, pathname: string) => boolean;
}

/**
 * A step that sits off the main line (the paused steps) carries this as its `stepNumber` instead
 * of a position. It can never advance the furthest mark, and in events it defers to where the user
 * actually is.
 *
 * Lives here, in the leaf every layer already imports, rather than in `analytics.ts`: a config
 * importing analytics would close a cycle through the registry.
 */
export const OFF_LINE_STEP_NUMBER = -1;
